import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getComicById, getRelatedComics } from '@/mocks/comics';
import ComicCard from '@/pages/home/components/ComicCard';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comic = id ? getComicById(parseInt(id)) : undefined;
  const relatedComics = comic ? getRelatedComics(comic) : [];

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-600 mb-6">抱歉，您查找的漫画不存在或已下架。</p>
            <Link to="/" className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: `${comic.title} - 韩漫网`,
        description: comic.description,
        author: {
          "@type": "Person",
          name: comic.author,
        },
        datePublished: comic.updateDate,
        dateModified: comic.updateDate,
        image: comic.cover,
        url: `https://www.hanmanwang.com/article/${comic.id}`,
        publisher: {
          "@type": "Organization",
          name: "韩漫网",
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "首页",
            item: "https://www.hanmanwang.com",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: `${comic.category}漫画`,
            item: `https://www.hanmanwang.com/category/${comic.categorySlug}`,
          },
          {
            "@type": "ListItem",
            position: 3,
            name: comic.title,
          },
        ],
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16 bg-background-50">
        <article className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: `${comic.category}漫画`, href: `/category/${comic.categorySlug}` },
              { label: comic.title },
            ]}
          />

          <div className="flex flex-col lg:flex-row gap-8 md:gap-10 mt-4">
            <div className="lg:w-[340px] flex-shrink-0">
              <div className="sticky top-20">
                <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-background-100">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 韩漫网 在线漫画 韩国漫画`}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-3 left-3 flex gap-1">
                    {comic.isHot && (
                      <span className="px-2 py-0.5 bg-primary-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
                        热门
                      </span>
                    )}
                    {comic.isNew && (
                      <span className="px-2 py-0.5 bg-accent-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
                        新作
                      </span>
                    )}
                  </div>
                </div>

                <div className="mt-4 p-4 bg-background-100 rounded-xl">
                  <Link
                    to={`/category/${comic.categorySlug}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-secondary-100 text-secondary-700 text-sm font-medium rounded-full hover:bg-secondary-200 transition-colors whitespace-nowrap mb-3"
                  >
                    {comic.category}
                  </Link>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-foreground-500">作者</span>
                      <span className="text-foreground-800 font-medium">{comic.author}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-500">状态</span>
                      <span className="text-primary-500 font-medium">{comic.status}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-500">最新章节</span>
                      <span className="text-foreground-800 font-medium">第{comic.chapters}话</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-500">评分</span>
                      <span className="text-accent-500 font-semibold">{comic.rating}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-500">阅读量</span>
                      <span className="text-foreground-800">{(comic.views / 10000).toFixed(0)}万</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-500">更新日期</span>
                      <span className="text-foreground-800">{comic.updateDate}</span>
                    </div>
                  </div>

                  <button className="w-full mt-4 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-full transition-all duration-300 hover:scale-105 whitespace-nowrap">
                    开始阅读
                  </button>
                </div>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-3">
                {comic.title}
              </h1>

              <div className="flex flex-wrap gap-2 mb-6">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="bg-background-100 rounded-xl p-5 md:p-6 mb-8">
                <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-3">
                  作品简介
                </h2>
                <div className="text-sm md:text-base text-foreground-700 leading-relaxed whitespace-pre-line">
                  {comic.longDescription}
                </div>
              </div>

              <div className="bg-background-100 rounded-xl p-5 md:p-6 mb-8">
                <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-4">
                  章节列表
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {Array.from({ length: Math.min(comic.chapters, 20) }, (_, i) => (
                    <button
                      key={i}
                      className="px-3 py-2 text-sm text-foreground-700 bg-background-50 hover:bg-primary-50 hover:text-primary-500 rounded-lg transition-colors text-left whitespace-nowrap"
                    >
                      第{comic.chapters - i}话
                    </button>
                  ))}
                  {comic.chapters > 20 && (
                    <button className="px-3 py-2 text-sm text-foreground-400 bg-background-50 rounded-lg text-center whitespace-nowrap">
                      ...更多章节
                    </button>
                  )}
                </div>
              </div>

              {relatedComics.length > 0 && (
                <div>
                  <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-5">
                    相关推荐
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {relatedComics.map((rel) => (
                      <ComicCard key={rel.id} comic={rel} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}